import { useEffect, useState } from 'react';
import { supabase } from '../../lib/supabase';

interface DriverHOS {
  id: number;
  driverId: string;
  driverName: string;
  truckName: string | null;
  dutyStatus: string | null;
  dutyStatusStartTime: string | null;
  shiftWorkMinutes: number;
  shiftDriveMinutes: number;
  cycleWorkMinutes: number;
  maxShiftWorkMinutes: number;
  maxShiftDriveMinutes: number;
  maxCycleWorkMinutes: number;
  mustEndShiftBy: string | null;
  mustEndShiftDrivingBy: string | null;
  mustEndCycleWorkBy: string | null;
}

const DUTY_STATUS_LABELS: Record<string, string> = {
  'OFF': 'Off Duty',
  'SB': 'Sleeper Berth',
  'ON': 'On Duty',
  'D': 'Driving',
  'YM': 'Yard Move',
  'PC': 'Personal Conveyance',
};

const DUTY_STATUS_COLORS: Record<string, string> = {
  'OFF': 'bg-gray-100 text-gray-800',
  'SB': 'bg-blue-100 text-blue-800',
  'ON': 'bg-yellow-100 text-yellow-800',
  'D': 'bg-green-100 text-green-800',
  'YM': 'bg-purple-100 text-purple-800',
  'PC': 'bg-indigo-100 text-indigo-800',
};

function formatMinutes(minutes: number): string {
  const hours = Math.floor(minutes / 60);
  const mins = Math.floor(minutes % 60);
  return `${hours}h ${mins}m`;
}

function formatDateTime(dateStr: string | null): string {
  if (!dateStr) return 'N/A';
  const date = new Date(dateStr);
  return date.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
}

function getTimeRemaining(endTime: string | null): { text: string; isUrgent: boolean } {
  if (!endTime) return { text: 'N/A', isUrgent: false };
  
  const now = new Date();
  const end = new Date(endTime);
  const diffMs = end.getTime() - now.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  
  if (diffMins < 0) {
    return { text: 'EXPIRED', isUrgent: true };
  }
  
  const hours = Math.floor(diffMins / 60);
  const mins = diffMins % 60;
  const isUrgent = diffMins < 120; // Less than 2 hours
  
  return {
    text: `${hours}h ${mins}m`,
    isUrgent,
  };
}

export function ELDHoursView() {
  const [drivers, setDrivers] = useState<DriverHOS[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchDriverHours();
    // Refresh every 5 minutes
    const interval = setInterval(fetchDriverHours, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, []);

  async function fetchDriverHours() {
    try {
      setLoading(true);
      setError(null);

      // Call ProLogs proxy to get driver hours
      const response = await supabase.functions.invoke('prologs-proxy', {
        body: {
          endpoint: '/api/v1/drivers',
          method: 'GET',
        },
      });

      if (response.error) {
        throw new Error(response.error.message);
      }

      if (!response.data || !Array.isArray(response.data)) {
        throw new Error('Invalid response from ProLogs API');
      }

      setDrivers(response.data);
    } catch (err) {
      console.error('Error fetching driver hours:', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch driver hours');
    } finally {
      setLoading(false);
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-slate-600">Loading driver hours...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-6">
        <div className="flex items-start">
          <div className="flex-shrink-0">
            <svg className="h-6 w-6 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <div className="ml-3">
            <h3 className="text-sm font-medium text-red-800">Error Loading Driver Hours</h3>
            <p className="mt-2 text-sm text-red-700">{error}</p>
            <button
              onClick={fetchDriverHours}
              className="mt-3 text-sm font-medium text-red-600 hover:text-red-500"
            >
              Try Again
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (drivers.length === 0) {
    return (
      <div className="bg-slate-50 border border-slate-200 rounded-lg p-8 text-center">
        <p className="text-slate-600">No driver hours data available</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <div className="bg-white rounded-lg shadow p-4">
          <div className="text-sm text-slate-600">Total Drivers</div>
          <div className="text-2xl font-bold text-slate-900">{drivers.length}</div>
        </div>
        <div className="bg-white rounded-lg shadow p-4">
          <div className="text-sm text-slate-600">Currently Driving</div>
          <div className="text-2xl font-bold text-green-600">
            {drivers.filter(d => d.dutyStatus === 'D').length}
          </div>
        </div>
        <div className="bg-white rounded-lg shadow p-4">
          <div className="text-sm text-slate-600">Off Duty</div>
          <div className="text-2xl font-bold text-slate-600">
            {drivers.filter(d => d.dutyStatus === 'OFF' || d.dutyStatus === 'SB').length}
          </div>
        </div>
      </div>

      {/* Driver Hours Table */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-slate-200">
            <thead className="bg-slate-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                  Driver
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                  Shift Hours
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                  Drive Time
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                  Cycle Hours
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                  Time Remaining
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-slate-200">
              {drivers.map((driver) => {
                const shiftRemaining = getTimeRemaining(driver.mustEndShiftBy);
                const driveRemaining = getTimeRemaining(driver.mustEndShiftDrivingBy);
                const cycleRemaining = getTimeRemaining(driver.mustEndCycleWorkBy);

                return (
                  <tr key={driver.id} className="hover:bg-slate-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div>
                          <div className="text-sm font-medium text-slate-900">
                            {driver.driverName}
                          </div>
                          {driver.truckName && (
                            <div className="text-sm text-slate-500">
                              Truck: {driver.truckName}
                            </div>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${
                        DUTY_STATUS_COLORS[driver.dutyStatus || ''] || 'bg-gray-100 text-gray-800'
                      }`}>
                        {DUTY_STATUS_LABELS[driver.dutyStatus || ''] || driver.dutyStatus || 'Unknown'}
                      </span>
                      {driver.dutyStatusStartTime && (
                        <div className="text-xs text-slate-500 mt-1">
                          Since {formatDateTime(driver.dutyStatusStartTime)}
                        </div>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-900">
                      <div className="font-medium">
                        {formatMinutes(driver.shiftWorkMinutes)} / {formatMinutes(driver.maxShiftWorkMinutes)}
                      </div>
                      <div className="w-full bg-slate-200 rounded-full h-2 mt-1">
                        <div
                          className={`h-2 rounded-full ${
                            driver.shiftWorkMinutes / driver.maxShiftWorkMinutes > 0.9
                              ? 'bg-red-600'
                              : driver.shiftWorkMinutes / driver.maxShiftWorkMinutes > 0.75
                              ? 'bg-yellow-500'
                              : 'bg-green-500'
                          }`}
                          style={{
                            width: `${Math.min(100, (driver.shiftWorkMinutes / driver.maxShiftWorkMinutes) * 100)}%`,
                          }}
                        ></div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-900">
                      <div className="font-medium">
                        {formatMinutes(driver.shiftDriveMinutes)} / {formatMinutes(driver.maxShiftDriveMinutes)}
                      </div>
                      <div className="w-full bg-slate-200 rounded-full h-2 mt-1">
                        <div
                          className={`h-2 rounded-full ${
                            driver.shiftDriveMinutes / driver.maxShiftDriveMinutes > 0.9
                              ? 'bg-red-600'
                              : driver.shiftDriveMinutes / driver.maxShiftDriveMinutes > 0.75
                              ? 'bg-yellow-500'
                              : 'bg-green-500'
                          }`}
                          style={{
                            width: `${Math.min(100, (driver.shiftDriveMinutes / driver.maxShiftDriveMinutes) * 100)}%`,
                          }}
                        ></div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-900">
                      <div className="font-medium">
                        {formatMinutes(driver.cycleWorkMinutes)} / {formatMinutes(driver.maxCycleWorkMinutes)}
                      </div>
                      <div className="w-full bg-slate-200 rounded-full h-2 mt-1">
                        <div
                          className={`h-2 rounded-full ${
                            driver.cycleWorkMinutes / driver.maxCycleWorkMinutes > 0.9
                              ? 'bg-red-600'
                              : driver.cycleWorkMinutes / driver.maxCycleWorkMinutes > 0.75
                              ? 'bg-yellow-500'
                              : 'bg-green-500'
                          }`}
                          style={{
                            width: `${Math.min(100, (driver.cycleWorkMinutes / driver.maxCycleWorkMinutes) * 100)}%`,
                          }}
                        ></div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      <div className="space-y-1">
                        <div className={shiftRemaining.isUrgent ? 'text-red-600 font-semibold' : 'text-slate-900'}>
                          Shift: {shiftRemaining.text}
                        </div>
                        <div className={driveRemaining.isUrgent ? 'text-red-600 font-semibold' : 'text-slate-600'}>
                          Drive: {driveRemaining.text}
                        </div>
                        <div className={cycleRemaining.isUrgent ? 'text-red-600 font-semibold' : 'text-slate-600'}>
                          Cycle: {cycleRemaining.text}
                        </div>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Refresh Button */}
      <div className="flex justify-end">
        <button
          onClick={fetchDriverHours}
          disabled={loading}
          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {loading ? 'Refreshing...' : 'Refresh Data'}
        </button>
      </div>
    </div>
  );
}
